import React from "react";
import styled, { css } from "styled-components";
import { Gold, Platinum, Silver } from "common/Icons/Icons";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useTierSummary } from "features/affiliation/NewLook/useTierSummary";
import useReferredUsers from "features/affiliation/common/hooks/useReferredUsers";
import { AffiliationTabs } from "features/affiliation/common/types";
import { FigureLarge, Meta } from "features/affiliation/NewLook/roles";

/**
 * The commission tier: where you are (tier, rate, active users against the
 * next threshold) and the ladder of tiers, each segment filling as you
 * climb.
 */
const TierPanel = ({ className }: { className?: string }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { referralHistoryQuery } = useReferredUsers(
    AffiliationTabs.REFERRED_USERS
  );
  const {
    tiers,
    currentTier,
    progressPercent,
    commission,
    inviteText,
    activeUsers,
    activeUsersLabel,
    getTierName,
  } = useTierSummary();
  const loading = referralHistoryQuery.isLoading;

  return (
    <Panel
      className={className}
      title={t("tiers.panel_title")}
      flush={true}
      data-testid="affiliation-tier"
    >
      <Current>
        <Top>
          <Badge aria-hidden={true}>
            {TIER_TO_BADGE[currentTier] ?? TIER_TO_BADGE[1]}
          </Badge>
          <Names>
            {loading ? (
              <Skeleton width={120} height={16} />
            ) : (
              <TierName>
                {t("tiers.tier_with_name", {
                  tier: currentTier,
                  name: getTierName(currentTier),
                })}
              </TierName>
            )}
            {commission && <Meta>{commission}</Meta>}
          </Names>
          <Users>
            <FigureLarge>{activeUsers}</FigureLarge>
            <Meta>{activeUsersLabel}</Meta>
          </Users>
        </Top>
        <Track aria-hidden={true}>
          <Fill style={{ width: `${inviteText ? progressPercent : 100}%` }} />
        </Track>
        <Meta>{inviteText ?? t("tiers.top_tier")}</Meta>
      </Current>
      {tiers.length > 0 && (
        <Ladder>
          {tiers.map((tier, index) => (
            <Step
              key={tier.tier}
              $locked={tier.tier > currentTier}
              $current={tier.tier === currentTier}
            >
              <StepBadge aria-hidden={true}>
                {TIER_TO_BADGE[tier.tier]}
              </StepBadge>
              <StepName>{getTierName(tier.tier)}</StepName>
              <StepRange>
                {index === tiers.length - 1
                  ? t("tiers.users_from", { from: tier.bottom })
                  : t("tiers.users_range", {
                      from: tier.bottom,
                      to: tier.top,
                    })}
              </StepRange>
              <StepRate>
                {Math.round(tier.commission_rate * 1000) / 10}%
              </StepRate>
            </Step>
          ))}
        </Ladder>
      )}
    </Panel>
  );
};

export default TierPanel;

const TIER_TO_BADGE: Record<number, React.JSX.Element> = {
  1: <Silver />,
  2: <Gold />,
  3: <Platinum />,
};

const Current = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
`;
const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;
const Badge = styled.div`
  flex: none;
  width: 32px;
  height: 34px;
  svg {
    width: 100%;
    height: 100%;
  }
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const TierName = styled.div`
  ${newLookType.figure};
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Users = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
`;
const Track = styled.div`
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  overflow: hidden;
`;
const Fill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: ${(p) => getNewLookTokens(p.theme).accent};
  transition: width 300ms ease;
`;
const Ladder = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const StepBadge = styled.span`
  flex: none;
  width: 16px;
  height: 17px;
  display: inline-flex;
  svg {
    width: 100%;
    height: 100%;
  }
`;
const StepName = styled.span`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const StepRange = styled.span`
  flex: 1;
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StepRate = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Step = styled.li<{ $locked: boolean; $current: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${(p) =>
    p.$locked &&
    css`
      ${StepBadge} {
        opacity: 0.45;
        filter: grayscale(0.6);
      }
    `}
  ${(p) =>
    p.$current &&
    css`
      ${StepName}, ${StepRate} {
        color: ${getNewLookTokens(p.theme).ink};
        font-weight: 500;
      }
    `}
`;
